import type { ComponentProps } from 'react';
import { css } from '@linaria/atomic';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { CheckboxField, Text } from '@frontend/design-system';

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-md);
    width: 100%;
  `,
  section: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,
  checkbox: css`
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    padding-left: var(--fabric-space-padding-md);
    background: var(--fabric-color-neutral-5);
    border: solid 1px var(--fabric-color-neutral-20);
    border-radius: var(--fabric-radius-md);
    label {
      padding: var(--fabric-space-padding-md);
      padding-left: 0;
      cursor: pointer;
    }
  `,
};

export type CareCreditMessageConsentProps = {
  checkboxProps: Omit<ComponentProps<typeof CheckboxField>, 'label' | 'name'>;
  isPayment: boolean;
  phoneNumber?: string;
};

export const CareCreditMessageConsent = ({ checkboxProps, isPayment, phoneNumber }: CareCreditMessageConsentProps) => {
  const { t } = useTranslation('payments');
  const formattedPhoneNumber = formatPhoneNumber(phoneNumber || '');

  return (
    <div className={styles.body}>
      <Text>
        {isPayment
          ? t("Before sending a secure CareCredit payment link, you need your customer's consent to receive texts.")
          : t(
              "Before sending a secure CareCredit application link, you need your customer's consent to receive texts."
            )}
      </Text>
      <div className={styles.section}>
        <Text weight='bold'>{t("Customer's mobile number")}</Text>
        {!!formattedPhoneNumber ? (
          <Text>{formattedPhoneNumber}</Text>
        ) : (
          <Text color='error'>{t('No valid phone number')}</Text>
        )}
      </div>
      <div className={styles.section}>
        <Text weight='bold'>{t('Read to customer:')}</Text>
        <Text>
          {isPayment ? (
            <Trans>
              "We'll send a one-time payment link to the mobile number we have on file for you. Message and data rates
              may apply. For Terms & Conditions and Privacy Policy, visit Synchrony.com.
              <br />
              <br />
              If you understand and agree to what I have told you, please say YES."
            </Trans>
          ) : (
            <Trans>
              "We'll send a one-time application link to the mobile number we have on file for you. Message and data
              rates may apply. For Terms & Conditions and Privacy Policy, visit Synchrony.com.
              <br />
              <br />
              If you understand and agree to what I have told you, please say YES."
            </Trans>
          )}
        </Text>
      </div>

      <CheckboxField
        {...checkboxProps}
        className={styles.checkbox}
        name='agree'
        label={t('Customer consents to receiving text.')}
      />
    </div>
  );
};
